import { computed, inject, onScopeDispose, provide, reactive, readonly, shallowRef, toRaw, watchEffect } from 'vue';

import { useToggleScope } from '@components/composable';
import { getCurrentInstance } from '@components/utils';

import type { ComputedRef, InjectionKey, Ref } from 'vue';

export interface IUseStack {
  globalTop: Readonly<Ref<boolean>>;
  localTop: ComputedRef<boolean>;
  stackStyles: ComputedRef<{ zIndex: number }>;
}

type TStackProvider = {
  activeChildren: Set<number>;
};

const StackSymbol: InjectionKey<TStackProvider> = Symbol.for('cds:stack');
const globalStack = reactive<[uid: number, zIndex: number][]>([]);

export function useStack(
  isActive: Readonly<Ref<boolean>>,
  zIndex: Readonly<Ref<string | number>>,
  disableGlobalStack: boolean = false
): IUseStack {
  const vm = getCurrentInstance('useStack');
  const createStackEntry = !disableGlobalStack;
  const parent = inject(StackSymbol, undefined);
  const stack: TStackProvider = reactive({
    activeChildren: new Set<number>()
  });
  const globalTop = shallowRef(true);
  const _zIndex = shallowRef(+zIndex.value);

  provide(StackSymbol, stack);

  useToggleScope(isActive, () => {
    const lastZIndex = globalStack.at(-1)?.[1];
    _zIndex.value = lastZIndex ? lastZIndex + 10 : +zIndex.value;

    if (createStackEntry) {
      globalStack.push([vm.uid, _zIndex.value]);
    }

    parent?.activeChildren.add(vm.uid);

    onScopeDispose(() => {
      if (createStackEntry) {
        const idx = toRaw(globalStack).findIndex((value) => value[0] === vm.uid);
        globalStack.splice(idx, 1);
      }

      parent?.activeChildren.delete(vm.uid);
    });
  });

  if (createStackEntry) {
    watchEffect(() => {
      const _isTop = globalStack.at(-1)?.[0] === vm.uid;
      setTimeout(() => (globalTop.value = _isTop));
    });
  }

  const localTop = computed(() => !stack.activeChildren.size);

  return {
    globalTop: readonly(globalTop),
    localTop,
    stackStyles: computed(() => ({ zIndex: _zIndex.value }))
  };
}
